export type PushConfig = {
  supported: boolean;
  public_key?: string | null;
  subscribed?: boolean;
  inactivity_hours?: number;
};

function base64UrlToUint8Array(base64Url: string) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export function installedAppMode() {
  if (typeof window === "undefined") return false;
  const standaloneDisplay = window.matchMedia?.("(display-mode: standalone)").matches === true;
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return standaloneDisplay || iosNavigator.standalone === true;
}

export async function getPushConfig(): Promise<PushConfig> {
  try {
    const response = await fetch("/app-api/push/config", { cache: "no-store" });
    if (!response.ok) return { supported: false, subscribed: false };
    return response.json();
  } catch {
    return { supported: false, subscribed: false };
  }
}

export async function enablePushNotifications() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    throw new Error("Push notifications are not supported on this device.");
  }
  if (!installedAppMode()) {
    throw new Error("On iPhone, add CatchCompass to your Home Screen first, then enable session alerts from the installed app.");
  }

  const config = await getPushConfig();
  if (!config.supported || !config.public_key) {
    throw new Error("CatchCompass push notifications are not configured yet.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(config.public_key),
    });
  }

  const response = await fetch("/app-api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(subscription.toJSON()),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || "Could not enable session alerts.");
  return true;
}

export async function disablePushNotifications() {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager?.getSubscription();
  const endpoint = subscription?.endpoint ?? "";
  await fetch("/app-api/push/subscribe", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint }),
  }).catch(() => undefined);
  if (subscription) await subscription.unsubscribe().catch(() => false);
}
